import {
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
  Box,
  List,
  ListItemButton,
  styled,
} from '@mui/material';
import { CSSProperties, ReactNode, useEffect, useMemo, useState } from 'react';
import { Element, Link, animateScroll as scroll } from 'react-scroll';

const StyledListItemButton = styled(ListItemButton)({
  borderRadius: 8,
  paddingBlock: 10,
  fontSize: 14,

  '&.Mui-selected': {
    backgroundColor: '#fff',
  },
});

type NavigationLeafItem = {
  key: string;
  href: string; // id selector beginning with '#'
  title: ReactNode;
  body?: ReactNode;
  bodyStyle?: CSSProperties;
  hide?: boolean;
};

type NavigationSectionItem = {
  key: string;
  title: ReactNode;
  children: NavigationLeafItem[];
  hide?: boolean;
};

type SettingsSplitNavigationProps = {
  navigation: Array<NavigationLeafItem | NavigationSectionItem>;
  targetOffset?: number; // how much to offset when scrolling/activating
  offsetTop?: number; // sticky top for the left nav
  style?: CSSProperties;
  sections?: boolean; // when true, navigation is grouped by sections with children
};

const ensureHash = (href: string) => (href.startsWith('#') ? href : `#${href}`);
const removeHash = (href: string) => (href.startsWith('#') ? href.slice(1) : href);

const SettingSplitNavigation = (props: SettingsSplitNavigationProps) => {
  const { navigation, targetOffset = 150, offsetTop = 0, style, sections } = props;
  const [activeSection, setActiveSection] = useState<number>(0);
  const [activeHref, setActiveHref] = useState<string>('');

  const filteredNavigation = useMemo(
    () => navigation.filter((i) => !('hide' in i && i.hide)),
    [navigation],
  );

  const navigationToIndex = useMemo(() => {
    if (!sections) return {} as Record<string, string[]>;
    return (filteredNavigation as NavigationSectionItem[]).reduce(
      (acc, item, key) => ({
        ...acc,
        [String(key)]: item.children.map((child) => removeHash(child.href)),
      }),
      {} as Record<string, string[]>,
    );
  }, [filteredNavigation, sections]);

  const allElementIds = useMemo(() => {
    if (sections) {
      return (filteredNavigation as NavigationSectionItem[]).flatMap((s) =>
        s.children.map((c) => removeHash(c.href)),
      );
    }
    return (filteredNavigation as NavigationLeafItem[]).map((i) => removeHash(i.href));
  }, [filteredNavigation, sections]);

  // Bottom-of-page detection to force last section active
  useEffect(() => {
    if (allElementIds.length === 0) return;

    let scrollTimer: NodeJS.Timeout;

    const handleScroll = () => {
      // Debounce to avoid excessive checks
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        const scrollTop = window.scrollY;
        const windowHeight = window.innerHeight;
        const docHeight = document.documentElement.scrollHeight;

        // Check if we're at the bottom (with 20px threshold for safety)
        const isAtBottom = scrollTop + windowHeight >= docHeight - 20;

        if (isAtBottom) {
          const lastId = allElementIds[allElementIds.length - 1];
          const lastHash = ensureHash(lastId);

          // Only update if not already on last section (prevents unnecessary re-renders)
          if (activeHref !== lastHash) {
            setActiveHref(lastHash);

            if (sections) {
              const idx = Object.entries(navigationToIndex).reduce((acc, [key, items]) => {
                if (items.includes(lastId)) acc = Number(key);
                return acc;
              }, 0);
              setActiveSection(idx);
            }
          }
        }
      }, 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Run once on mount to check initial state
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimer);
    };
  }, [allElementIds, activeHref, sections, navigationToIndex]);

  // Initialize active state on mount - set first section as active if at top
  useEffect(() => {
    const hash = window.location.hash;

    // If there's a hash in URL, use it
    if (hash && hash !== '#') {
      const elementId = removeHash(hash);
      setTimeout(() => {
        const element = document.getElementById(elementId);
        if (element) {
          const offset = Math.max(targetOffset, offsetTop);
          scroll.scrollTo(element.offsetTop - offset, {
            smooth: true,
            duration: 500,
          });
          setActiveHref(hash);
          if (sections) {
            const idx = Object.entries(navigationToIndex).reduce((acc, [key, items]) => {
              if (items.includes(elementId)) acc = Number(key);
              return acc;
            }, 0);
            setActiveSection(idx);
          }
        }
      }, 100);
      return;
    }

    // If no hash and we're at the top, set first section as active
    const isAtTop = window.scrollY < 100;
    if (isAtTop && allElementIds.length > 0) {
      const firstId = allElementIds[0];
      const firstHash = ensureHash(firstId);
      setActiveHref(firstHash);
      if (sections) {
        const idx = Object.entries(navigationToIndex).reduce((acc, [key, items]) => {
          if (items.includes(firstId)) acc = Number(key);
          return acc;
        }, 0);
        setActiveSection(idx);
      }
    }
    // eslint-disable-next-line
  }, []);

  const renderCard = (item: NavigationLeafItem, idx: number) => {
    const elementId = removeHash(item.href);
    return (
      <Element key={idx} id={elementId} name={elementId}>
        <Paper elevation={0} style={item.bodyStyle} data-testid={`settings-section-${elementId}`}>
          <Typography variant='h6' px={3} py={2}>
            {item.title}
          </Typography>
          <Divider />
          {item.body}
        </Paper>
      </Element>
    );
  };

  const renderLeafNavItem = (item: NavigationLeafItem) => {
    const elementId = removeHash(item.href);
    const hash = ensureHash(item.href);
    const selected = activeHref ? activeHref === hash : false;

    return (
      <Link
        key={item.key}
        to={elementId}
        spy={true}
        smooth={true}
        duration={500}
        offset={-Math.max(targetOffset, offsetTop)}
        onSetActive={() => setActiveHref(hash)}
      >
        <StyledListItemButton selected={selected} data-testid={`settings-nav-${item.key}`}>
          <Typography variant='subtitle2' fontWeight={selected ? 700 : 400}>
            {item.title}
          </Typography>
        </StyledListItemButton>
      </Link>
    );
  };

  return (
    <Grid container spacing={4} style={style}>
      <Grid size={3.5}>
        <Box sx={{ position: 'sticky', top: offsetTop }}>
          <Stack spacing={0.5} component={List} sx={{ padding: 0 }}>
            {sections
              ? (filteredNavigation as NavigationSectionItem[]).map((sectionItem) => (
                  <Box key={sectionItem.key}>
                    <Box px={2} py={1.5}>
                      <Typography variant='subtitle2' fontWeight={700}>
                        {sectionItem.title}
                      </Typography>
                    </Box>
                    {(sectionItem.children || []).map((child) => (
                      <Box key={child.key} pl={2}>
                        {renderLeafNavItem(child)}
                      </Box>
                    ))}
                  </Box>
                ))
              : (filteredNavigation as NavigationLeafItem[]).map(renderLeafNavItem)}
          </Stack>
        </Box>
      </Grid>
      <Grid size={8.5} style={{ paddingBottom: 200 }}>
        <Stack spacing={3}>
          {sections
            ? (filteredNavigation as NavigationSectionItem[])[activeSection]?.children.map(
                renderCard,
              )
            : (filteredNavigation as NavigationLeafItem[]).map(renderCard)}
        </Stack>
      </Grid>
    </Grid>
  );
};

export default SettingSplitNavigation;
